/**
 * Cloned from Shell App https://github.com/MGMResorts/web-mfe-shell/blob/main/src/common/context/FlagsContext.tsx
 */
import { FeatureFlagsSdk, type FeatureFlagsSdkTypes } from '@mgmresorts/client-feature-flagging';
import React, { useState, useEffect, ReactNode, useMemo, useCallback } from 'react';

import { ClientIds } from '../../utils/constants';
import { actually } from '../../utils/logging';
import { getPersistedParameters } from '../../utils/persistParameterContext';

import { useFeatureFlagsValue } from './FlagsValueContext';
import defaultFlags from './defaultFlags.json';

export type Flags = Awaited<ReturnType<typeof FeatureFlagsSdk.getFeatureFlags>>;

interface FlagsProviderProps {
  children: ReactNode;
}

interface FlagsData {
  flags: Flags;
  isFulfilled: boolean;
}

export interface FlagsContextT {
  flags: Flags;
  getFlagByKey: (key: string) => Flags[number]['flagValue'];
}

export const FlagsContext = React.createContext<FlagsContextT>({
  flags: [],
  getFlagByKey: () => undefined,
});

export const FlagsProvider = ({ children }: FlagsProviderProps) => {
  const { setFlags } = useFeatureFlagsValue();

  const [flagsData, setFlagsData] = useState<FlagsData>({ flags: [], isFulfilled: false });
  const params = getPersistedParameters();
  const clientId = params?.client_id ?? '';
  const isFallbackEnabledClient = [
    ClientIds.MGM_APP_WEB,
    ClientIds.HOLIDAY_GIFT_SHOP,
    ClientIds.CRE_BORGATA_CUSTOMER,
  ].includes(clientId as ClientIds);

  useEffect(() => {
    if (process.env.IN_JEST === 'true') {
      setFlags([]);
      return;
    }

    const rawEnv = process.env.REACT_APP_ENVIRONMENT?.toUpperCase();
    const environment = (rawEnv === 'DEV' ? 'UAT' : rawEnv ?? 'PROD') as FeatureFlagsSdkTypes.Environment;

    const fetchFlagsAsync = async () => {
      try {
        const fetchedFlags = await FeatureFlagsSdk.getFeatureFlags({
          environment,
          name: 'mgm-identity-oidc-app',
        });
        if (fetchedFlags) {
          setFlagsData({ flags: fetchedFlags, isFulfilled: true });
          setFlags(fetchedFlags);
        } else {
          const fallbackFlags = isFallbackEnabledClient ? defaultFlags : [];
          setFlagsData({ flags: fallbackFlags as Flags, isFulfilled: true });
          setFlags(fallbackFlags as Flags);
        }
      } catch (err) {
        actually.error(err);
        const fallbackFlags = isFallbackEnabledClient ? defaultFlags : [];
        setFlagsData({ flags: fallbackFlags as Flags, isFulfilled: true });
        setFlags(fallbackFlags as Flags);
      }
    };

    fetchFlagsAsync().catch(e => actually.error(e));
  }, [setFlags, isFallbackEnabledClient]);

  const getFlagByKey: FlagsContextT['getFlagByKey'] = useCallback(
    (key: string) => flagsData.flags?.find(x => x?.flagKey === key)?.flagValue,
    [flagsData.flags],
  );

  const value: FlagsContextT = useMemo(() => {
    return {
      flags: flagsData.flags,
      getFlagByKey,
    };
  }, [flagsData, getFlagByKey]);

  return <FlagsContext.Provider value={value}>{children}</FlagsContext.Provider>;
};

export const useFeatureFlags = () => {
  const context = React.useContext(FlagsContext);

  if (context === undefined) {
    throw new Error('useFlags must be used within a FlagsProvider');
  }

  return context;
};

export enum FEATURE_FLAGS {
  OKTA_IDX_API = 'IDENT-9479_enable-okta-idx-api_2026-01-21_temporary',
  ENABLE_MFA = 'IDENT-9543_enable-mfa_2026-01-28_temporary',
  ENABLE_PASSKEY = 'IDENT-10035_enable-passkey_2026-03-23_temporary',
  ENABLE_POST_SIGNIN_2FA_ENROLLMENT = 'PROSERV-2431',
  PREVENT_AUTO_SIGN_IN = 'PROSERV-2250',
  REMOVE_2FA_SKIP_FOR_NOW = 'PROSERV-2513',
}
